import './ServiceOrderShow.scss';

import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { Show, TextField } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tabs, Tag } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCountryEnforcer } from '@/components/general/CountryEnforcer';
import { ShowHeader } from '@/components/general/ShowInfo/ShowHeader';
import ServiceOrderStatusTag from '@/components/serviceOrder/ServiceOrderStatusTag';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

import ServiceOrderShowHeaderButtons from './ServiceOrderShow/ServiceOrderShowHeaderButtons';
import ServiceOrderShowLogistic from './ServiceOrderShow/ServiceOrderShowLogistic';
import ServiceOrderShowServiceInfo from './ServiceOrderShow/ServiceOrderShowServiceInfo';

const ServiceOrderShow = () => {
  const { alert, blockEditForCountry } = useCountryEnforcer();
  const { userWorkArea } = useUserContext();
  const { serviceOrderRepository } = useRepositoriesContext();
  const { t } = useTranslation();
  const {
    result: serviceOrder,
    query: { isLoading, isFetching, refetch },
  } = useShow<ServiceOrder>();
  const [serviceOrderItems, setServiceOrderItems] = useState<ServiceOrderItem[]>([]);
  const [modifingAdminData, setModifingAdminData] = useState(false);
  const refetchAll = async () => {
    await refetch();
    if (serviceOrder?.id) {
      const items = await serviceOrderRepository.getServiceOrderItems(serviceOrder.id);

      setServiceOrderItems(items);
    }
  };

  const hasTechnicianItem = useMemo(
    () =>
      serviceOrderItems.some(
        item => item.stock_technician_amount || item.package_item == undefined,
      ),
    [serviceOrderItems],
  );

  return (
    <Show
      isLoading={isLoading}
      headerButtons={({ refreshButtonProps, deleteButtonProps }) =>
        serviceOrder && (
          <ServiceOrderShowHeaderButtons
            serviceOrder={serviceOrder}
            serviceOrderItems={serviceOrderItems}
            isRefreshing={isFetching}
            refineButtonsProps={{
              refreshButtonProps: { ...refreshButtonProps, onClick: () => void refetchAll() },
              deleteButtonProps,
              listButtonProps: undefined,
            }}
            refetch={() => void refetchAll()}
            blockForCountry={blockEditForCountry}
            modifingAdminData={modifingAdminData}
          />
        )
      }>
      {alert}
      {serviceOrder && (
        <div className="ServiceOrderShow">
          <ShowHeader
            title={
              <>
                {t('Orden de servicio')} #{serviceOrder?.id}
              </>
            }
            statusTag={
              serviceOrder?.status != undefined ? (
                <ServiceOrderStatusTag serviceOrderStatus={serviceOrder.status} />
              ) : undefined
            }
            extraTags={
              serviceOrder.created_automatically ? (
                <Tag color="geekblue">{t('Automática')}</Tag>
              ) : undefined
            }
            createdAt={serviceOrder?.created_at}
            updatedAt={serviceOrder?.updated_at}
            createdBy={
              <TextField
                value={
                  serviceOrder.created_by
                    ? `${(serviceOrder as any).created_by_str} (${serviceOrder.created_by})`
                    : ''
                }
              />
            }
          />
          {!hasTechnicianItem ||
          (userWorkArea != USER_WORK_AREA.LOGISTICS && userWorkArea != USER_WORK_AREA.SOFTWARE) ? (
            <ServiceOrderShowServiceInfo
              serviceOrder={serviceOrder}
              serviceOrderItems={serviceOrderItems}
              setServiceOrderItems={setServiceOrderItems}
              blockEdit={blockEditForCountry}
              refetch={refetchAll}
              modifingAdminData={modifingAdminData}
              setModifingAdminData={setModifingAdminData}
            />
          ) : (
            <Tabs
              defaultActiveKey="1"
              items={[
                {
                  label: t('Servicio realizado'),
                  key: '1',
                  children: (
                    <ServiceOrderShowServiceInfo
                      serviceOrder={serviceOrder}
                      serviceOrderItems={serviceOrderItems}
                      setServiceOrderItems={setServiceOrderItems}
                      blockEdit={blockEditForCountry}
                      refetch={refetchAll}
                      modifingAdminData={modifingAdminData}
                      setModifingAdminData={setModifingAdminData}
                    />
                  ),
                },
                {
                  label: t('Logística'),
                  key: '2',
                  children: (
                    <ServiceOrderShowLogistic
                      serviceOrder={serviceOrder}
                      serviceOrderItems={serviceOrderItems}
                      setServiceOrderItems={setServiceOrderItems}
                      refetch={refetchAll}
                    />
                  ),
                  icon: serviceOrder.stock_technician_reviewed ? (
                    <CheckCircleFilled className="ServiceOrderShowLogisticIcon Success" />
                  ) : (
                    <WarningFilled className="ServiceOrderShowLogisticIcon Warning" />
                  ),
                },
              ]}
            />
          )}
        </div>
      )}
    </Show>
  );
};

export default ServiceOrderShow;
